import { useState, useEffect } from 'react';
import { Users, UserCheck, IndianRupee, ChevronRight, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_active_members: 0,
    total_members: 0,
    total_payments: 0,
    payments_this_month: 0
  });
  const [recentMembers, setRecentMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        const [statsRes, membersRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { headers }),
          axios.get(`${API}/members?active_only=false`, { headers })
        ]);

        setStats(statsRes.data);
        setRecentMembers(membersRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  const statCards = [
    {
      title: 'Total Members',
      value: stats.total_members,
      icon: Users,
      color: 'text-[#FF3B30]',
      bgColor: 'bg-[#FF3B30]/10'
    },
    {
      title: 'Active Members',
      value: stats.total_active_members,
      icon: UserCheck,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-400/10'
    },
    {
      title: 'Total Collection',
      value: `₹${stats.total_payments.toLocaleString()}`,
      icon: IndianRupee,
      color: 'text-amber-400',
      bgColor: 'bg-amber-400/10'
    },
    {
      title: 'This Month',
      value: `₹${stats.payments_this_month.toLocaleString()}`,
      icon: IndianRupee,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-400/10'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Barlow_Condensed'] font-extrabold text-3xl lg:text-4xl uppercase tracking-tight text-white">
            Dashboard
          </h1>
          <p className="text-zinc-400 mt-1">Overview of your gym</p>
        </div>
        <Button
          data-testid="add-member-btn"
          onClick={() => navigate('/dashboard/members')}
          className="bg-[#FF3B30] hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider rounded-none"
        >
          Manage Members
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={index}
            data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            className="stat-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 ${stat.bgColor} flex items-center justify-center`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1">
              {stat.title}
            </p>
            <p className={`font-['Barlow_Condensed'] font-extrabold text-2xl lg:text-3xl ${stat.color}`}>
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      {/* Recent Members */}
      <div className="card-glass">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-['Barlow_Condensed'] font-bold text-xl uppercase tracking-tight text-white">
            Recent Members
          </h2>
          <Button
            onClick={() => navigate('/dashboard/members')}
            variant="ghost"
            className="text-zinc-400 hover:text-white text-sm"
          >
            View All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        
        {recentMembers.length === 0 ? (
          <p className="text-zinc-500 text-center py-8">No members yet. Add your first member!</p>
        ) : (
          <div className="space-y-3">
            {recentMembers.map((member) => (
              <div
                key={member.id}
                data-testid={`recent-member-${member.id}`}
                className={`flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors ${!member.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${member.is_active ? 'bg-[#FF3B30]/20' : 'bg-zinc-700/20'} flex items-center justify-center`}>
                    <span className={`font-['Barlow_Condensed'] font-bold ${member.is_active ? 'text-[#FF3B30]' : 'text-zinc-500'}`}>
                      {member.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-white">{member.name}</p>
                    <p className="text-xs text-zinc-500">{member.membership_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-xs px-2 py-1 ${member.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                    {member.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <a 
                    href={`tel:${member.phone}`}
                    className="text-xs text-zinc-500 hover:text-[#FF3B30] flex items-center gap-1 justify-end mt-1"
                  >
                    <Phone className="w-3 h-3" />
                    {member.phone}
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
